// SPDX-License-Identifier: GPL-3.0-or-later
// A small canvas seam. The renderer draws labels on canvases. A test passes a fake factory.
// The default factory uses the page canvas, else an offscreen canvas, else nothing.

/** The part of a 2D context that the label code uses. */
export interface Ctx2DLike {
  font: string;
  fillStyle: string;
  strokeStyle: string;
  lineWidth: number;
  textAlign: string;
  textBaseline: string;
  clearRect(x: number, y: number, w: number, h: number): void;
  fillText(text: string, x: number, y: number): void;
  strokeText(text: string, x: number, y: number): void;
}

/** The part of a canvas that the label code uses. */
export interface CanvasLike {
  width: number;
  height: number;
  getContext(kind: "2d"): Ctx2DLike | null;
}

/** Makes a canvas, or null when the platform has none. */
export type CreateCanvas = (width: number, height: number) => CanvasLike | null;

/** The default factory. */
export const defaultCreateCanvas: CreateCanvas = (width, height) => {
  if (typeof document !== "undefined") {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    return canvas as unknown as CanvasLike;
  }
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(width, height) as unknown as CanvasLike;
  }
  return null;
};

/** Split a label into lines on spaces. */
export function labelLines(text: string): string[] {
  return text.split(" ").filter((line) => line.length > 0);
}

/** Width of a bold capital letter, as a part of the font size. */
const LETTER_WIDTH = 0.68;
/** Distance between two lines, as a part of the font size. */
const LINE_PITCH = 1.05;
/** The text box may fill this part of the ellipse that touches the canvas edges. */
const ELLIPSE_FILL = 0.9;
/** The largest font, as a part of the canvas height. One letter has this size. */
const FONT_MAX = 0.7;

/**
 * Font size in pixels so that the label fits in a canvas of `width` x `height` pixels. The
 * text box must stay inside the ellipse that touches the four canvas edges, because the
 * insert under the label is round. The default height is the width (a square canvas).
 */
export function labelFontSize(text: string, width: number, height: number = width): number {
  const lines = labelLines(text);
  const longest = lines.reduce((max, line) => Math.max(max, line.length), 1);
  const across = (LETTER_WIDTH * longest) / width;
  const down = (LINE_PITCH * Math.max(lines.length, 1)) / height;
  const fit = Math.sqrt(ELLIPSE_FILL) / Math.hypot(across, down);
  return Math.max(6, Math.min(height * FONT_MAX, fit));
}

/**
 * Draw a label. The text is white with a black edge, so it reads on a dark insert and on a
 * lit one. The text is the shape cue: the state never depends on color alone. `size` is the
 * canvas width. The canvas height is `height` (default: the width).
 */
export function drawLabel(ctx: Ctx2DLike, size: number, text: string, height: number = size): void {
  ctx.clearRect(0, 0, size, height);
  const lines = labelLines(text);
  if (lines.length === 0) return;
  const px = labelFontSize(text, size, height);
  ctx.font = `bold ${px}px sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.lineWidth = Math.max(2, px * 0.14);
  ctx.strokeStyle = "#000000";
  ctx.fillStyle = "#ffffff";
  const lineHeight = px * LINE_PITCH;
  const top = height / 2 - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((line, i) => {
    const y = top + i * lineHeight;
    ctx.strokeText(line, size / 2, y);
    ctx.fillText(line, size / 2, y);
  });
}
